import type { BaseError } from "./BaseError";
import type { ErrorClassification } from "./sentry/classification";
import type { ErrorSeverity } from "./sentry/severity";
import type { SentryErrorMetric } from "./types";

// Symbol.for keys are shared across realms and duplicate module copies, so the guards also match an instance
// built by another copy of @remi/error, which instanceof would reject.
const SENTRY_ERROR_BRAND = Symbol.for("@remi/error-core/SentryError");
const REMI_SERVICE_ERROR_BRAND = Symbol.for("@remi/error-core/RemiServiceError");

export interface SentryErrorData {
  fingerprint: string[];
  tags: Record<string, string | boolean | number>;
  attributes?: Record<string, unknown>;
}

export interface BrandedSentryError extends BaseError {
  readonly severity: ErrorSeverity;
  readonly service: string;
  readonly errorType: string;
  readonly publicMessage: string;
  readonly metric: SentryErrorMetric | undefined;
  readonly customFingerprintPrefix: string | undefined;
  getSentryData: () => SentryErrorData;
}

export interface BrandedRemiServiceError extends BrandedSentryError {
  readonly classification: ErrorClassification;
}

const setBrand = (error: BaseError, brand: symbol): void => {
  Object.defineProperty(error, brand, { value: true });
};

const hasBrand = (value: unknown, brand: symbol): boolean =>
  typeof value === "object" && value !== null && brand in value;

export const brandSentryError = (error: BrandedSentryError): void => setBrand(error, SENTRY_ERROR_BRAND);

export const brandRemiServiceError = (error: BrandedRemiServiceError): void =>
  setBrand(error, REMI_SERVICE_ERROR_BRAND);

export const isSentryError = (value: unknown): value is BrandedSentryError => hasBrand(value, SENTRY_ERROR_BRAND);

export const isRemiServiceError = (value: unknown): value is BrandedRemiServiceError =>
  hasBrand(value, REMI_SERVICE_ERROR_BRAND);
